import { useState, useMemo, useEffect } from 'react'
import {
  Stack, Text, Box, SimpleGrid, ThemeIcon, Group, Skeleton, Alert,
} from '@mantine/core'
import { WidgetCard, EmptyState } from '@/components/ui'
import { useDisclosure } from '@mantine/hooks'
import { notifications } from '@mantine/notifications'
import { IconUsersGroup, IconUserCheck, IconShield, IconAlertTriangle, IconClock, IconUsers, IconSearchOff } from '@tabler/icons-react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/api/client'
import { formatApiError } from '@/api/errors'
import { rolesApi, type Role } from '@/api/roles'
import { useAuthStore } from '@/store/auth'
import { Navigate } from 'react-router-dom'

import { UsersToolbar } from '@/components/admin/users/UsersToolbar'
import { UsersFilterPanel, type UserFilters, EMPTY_FILTERS, countActiveFilters } from '@/components/admin/users/UsersFilterPanel'
import { UsersTable, type UserRow } from '@/components/admin/users/UsersTable'
import { UsersBulkBar } from '@/components/admin/users/UsersBulkBar'
import { UserDrawer } from '@/components/admin/users/UserDrawer'
import { UserCreateModal } from '@/components/admin/users/UserCreateModal'
import { rowKey } from '@/components/admin/users/rowKey'

export type { UserRow }

/* ── KPI helper ── */
function KpiCard({ icon: Icon, color, value, label, style, onClick, active }: {
  icon: typeof IconUsersGroup; color: string; value: string | number; label: string;
  style?: React.CSSProperties; onClick?: () => void; active?: boolean;
}) {
  const interactive = !!onClick
  const baseStyle: React.CSSProperties = {
    ...(style || {}),
    ...(interactive ? { cursor: 'pointer', transition: 'transform 120ms ease, box-shadow 120ms ease' } : {}),
    ...(active ? { outline: '2px solid var(--ai-color-primary)', outlineOffset: 2 } : {}),
  }
  return (
    <WidgetCard
      accent={color === 'inovareTeal' ? 'primary' : 'none'}
      p="md"
      style={baseStyle}
      onClick={onClick}
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      onKeyDown={interactive ? (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick?.() }
      } : undefined}
      aria-pressed={interactive ? !!active : undefined}
    >
      <Group gap="md">
        <ThemeIcon size={44} radius="md" variant="light" color={color}>
          <Icon size={22} />
        </ThemeIcon>
        <Box>
          <Text size="xl" fw={700} c="var(--ai-text-primary)" style={{ lineHeight: 1 }}>{value}</Text>
          <Text size="xs" c="var(--ai-text-muted)" mt={2}>{label}</Text>
        </Box>
      </Group>
    </WidgetCard>
  )
}

export function Usuarios() {
  const { user } = useAuthStore()

  const qc = useQueryClient()
  const [createOpened, { open: openCreate, close: closeCreate }] = useDisclosure(false)
  const [selectedUser, setSelectedUser] = useState<UserRow | null>(null)
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set())
  const [searchQuery, setSearchQuery] = useState('')
  const [filters, setFilters] = useState<UserFilters>(EMPTY_FILTERS)
  const [filtersOpen, setFiltersOpen] = useState(false)

  /* ── Queries ── */
  // isError é OBRIGATÓRIO aqui: sem ele, uma falha (403 de permissão, 500) deixa
  // `data` undefined e a tela renderiza "Nenhum usuário cadastrado" + KPIs em 0 —
  // ou seja, afirma que a base está vazia quando na verdade não conseguiu ler.
  // Foi exatamente esse silêncio que escondeu um problema em produção.
  const { data, isLoading, isError, error } = useQuery<{ items: UserRow[] }>({
    queryKey: ['users'],
    queryFn: async () => (await api.get('/v1/users')).data,
    enabled: !!user?.id,
  })

  // Mantem selectedUser em sincronia com o resultado fresco de ['users'].
  // Necessario porque mutacoes (roles/permissions/template) invalidam a query,
  // mas selectedUser e snapshot em state — senao abas de tenants ficam stale
  // ate fechar/reabrir o drawer.
  // Casa por rowKey, não por id: cadastros pendentes têm `id: null`, então
  // `u.id === selectedUser.id` casava com o PRIMEIRO pendente da lista e podia
  // trocar o cadastro aberto no drawer por outro a cada refetch.
  useEffect(() => {
    if (!selectedUser || !data?.items) return
    const key = rowKey(selectedUser)
    const fresh = data.items.find((u) => rowKey(u) === key)
    if (fresh && fresh !== selectedUser) {
      setSelectedUser(fresh)
    }
  }, [data, selectedUser])
  const { data: allRoles = [] } = useQuery<Role[]>({
    queryKey: ['roles'],
    queryFn: () => rolesApi.listRoles(),
    enabled: !!user,
  })

  const users = data?.items ?? []

  /* ── Computed ── */
  const activeCount = useMemo(() => users.filter(u => u.is_active && u.status !== 'pending_approval').length, [users])
  const pendingCount = useMemo(() => users.filter(u => u.status === 'pending_approval').length, [users])
  const ldapCount = useMemo(() => users.filter(u => u.ldap_synced === true && !u.must_change_password).length, [users])
  const duplicateEmails = useMemo(() => {
    const counts = new Map<string, number>()
    for (const u of users) {
      const key = u.email.toLowerCase()
      counts.set(key, (counts.get(key) ?? 0) + 1)
    }
    return new Set([...counts.entries()].filter(([, c]) => c > 1).map(([e]) => e))
  }, [users])

  const filteredUsers = useMemo(() => {
    let result = users
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase()
      result = result.filter(u =>
        u.full_name?.toLowerCase().includes(q) ||
        u.email.toLowerCase().includes(q) ||
        u.roles?.some(r => r.toLowerCase().includes(q))
      )
    }
    if (filters.role && filters.role.length > 0)
      result = result.filter(u => u.roles?.some(r => filters.role!.includes(r)))
    if (filters.status === 'active') result = result.filter(u => u.is_active && u.status !== 'pending_approval')
    else if (filters.status === 'inactive') result = result.filter(u => !u.is_active && u.status !== 'pending_approval')
    else if (filters.status === 'pending_approval') result = result.filter(u => u.status === 'pending_approval')
    if (filters.kanidm === 'synced') result = result.filter(u => u.ldap_synced === true && !u.must_change_password)
    else if (filters.kanidm === 'pending') result = result.filter(u => u.ldap_synced !== true && !u.must_change_password)
    else if (filters.kanidm === 'awaiting') result = result.filter(u => !!u.must_change_password)
    return result
  }, [users, searchQuery, filters])

  /* ── Mutations ── */
  const toggleActiveMutation = useMutation({
    mutationFn: async ({ userId, isActive }: { userId: number; isActive: boolean }) => {
      await api.put(`/v1/users/${userId}`, { is_active: !isActive })
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['users'] })
      notifications.show({ color: 'teal', title: 'Atualizado', message: 'Status do usuário alterado.' })
    },
  })
  const syncLdapMutation = useMutation({
    mutationFn: async (userId: number) => (await api.post(`/v1/users/${userId}/sync-ldap`, {})).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['users'] })
      notifications.show({ color: 'teal', title: 'Sincronizado', message: 'Usuário criado no Kanidm.' })
    },
    onError: (err: any) => {
      notifications.show({ color: 'red', title: 'Erro', message: formatApiError(err, 'Erro ao sincronizar com Kanidm.') })
    },
  })

  const roleOptions = allRoles.map((r) => ({ value: r.code, label: r.name || r.code }))

  if (user?.role !== 'admin') return <Navigate to="/" replace />

  return (
    <Stack gap="lg">
      <SimpleGrid cols={{ base: 1, sm: (duplicateEmails.size > 0 ? 5 : pendingCount > 0 ? 4 : 3) }} spacing="md">
        <KpiCard
          icon={IconUsersGroup} color="inovareTeal"
          value={isLoading || isError ? '—' : users.length} label="Total de usuários"
          onClick={() => { setFilters(EMPTY_FILTERS); setSearchQuery('') }}
          active={!searchQuery && countActiveFilters(filters) === 0}
        />
        <KpiCard
          icon={IconUserCheck} color="teal"
          value={isLoading || isError ? '—' : activeCount} label="Ativos"
          onClick={() => { setFilters({ ...EMPTY_FILTERS, status: 'active' }); setSearchQuery('') }}
          active={filters.status === 'active'}
        />
        {pendingCount > 0 && (
          <KpiCard
            icon={IconClock} color="yellow"
            value={pendingCount} label="Pendentes de aprovação"
            onClick={() => { setFilters({ ...EMPTY_FILTERS, status: 'pending_approval' }); setSearchQuery('') }}
            active={filters.status === 'pending_approval'}
          />
        )}
        <KpiCard
          icon={IconShield} color="blue"
          value={isLoading || isError ? '—' : ldapCount} label="Kanidm sincronizados"
          onClick={() => { setFilters({ ...EMPTY_FILTERS, kanidm: 'synced' }); setSearchQuery('') }}
          active={filters.kanidm === 'synced'}
        />
        {duplicateEmails.size > 0 && (
          <KpiCard icon={IconAlertTriangle} color="red" value={duplicateEmails.size} label="Emails duplicados"
            style={{ background: 'var(--color-error-light)', border: '1px solid var(--color-error)' }} />
        )}
      </SimpleGrid>

      <UsersToolbar
        searchQuery={searchQuery} onSearchChange={setSearchQuery}
        filtersOpen={filtersOpen} onToggleFilters={() => setFiltersOpen(v => !v)}
        activeFilterCount={countActiveFilters(filters)} onNewUser={openCreate}
      />
      <UsersFilterPanel
        opened={filtersOpen} filters={filters} onFiltersChange={setFilters}
        roleOptions={roleOptions} onApply={() => setFiltersOpen(false)}
        onClear={() => { setFilters(EMPTY_FILTERS); setFiltersOpen(false) }}
      />

      {/* users={users} (não filteredUsers): a seleção sobrevive a mudanças de
          filtro, então usar a lista filtrada fazia a contagem (selectedIds.size)
          divergir dos cards exibidos. */}
      <UsersBulkBar selectedIds={selectedIds} users={users} onClearSelection={() => setSelectedIds(new Set())} />

      <WidgetCard accent="none" p={0}>
        {isLoading ? (
          <Box p="xl"><Skeleton height={300} /></Box>
        ) : isError ? (
          <Box p="xl">
            <Alert
              color="red"
              variant="light"
              icon={<IconAlertTriangle size={18} />}
              title="Não foi possível carregar os usuários"
            >
              <Text size="sm">
                {formatApiError(error, 'Falha ao consultar a lista de usuários.')}
              </Text>
              <Text size="xs" c="var(--ai-text-muted)" mt={6}>
                A lista pode existir e não estar visível para você — isto é um erro
                de leitura, não uma base vazia.
              </Text>
            </Alert>
          </Box>
        ) : filteredUsers.length === 0 ? (
          <EmptyState
            bare
            size="md"
            accent="info"
            icon={searchQuery ? IconSearchOff : IconUsers}
            title={searchQuery ? 'Nenhum usuário encontrado' : 'Nenhum usuário cadastrado'}
            description={searchQuery
              ? 'Nenhum usuário corresponde à busca atual. Tente ajustar o termo ou limpar o filtro.'
              : 'Cadastre o primeiro usuário para começar a gerenciar acessos e permissões do tenant.'}
          />
        ) : (
          <UsersTable
            users={filteredUsers} duplicateEmails={duplicateEmails}
            selectedIds={selectedIds} onSelectedIdsChange={setSelectedIds}
            selectedUserId={selectedUser?.id ?? null}
            selectedRowKey={selectedUser ? rowKey(selectedUser) : null}
            resetPageKey={`${searchQuery}|${JSON.stringify(filters)}`}
            onSelectUser={setSelectedUser}
            onToggleActive={(id, isActive) => toggleActiveMutation.mutate({ userId: id, isActive })}
            onSyncLdap={(id) => syncLdapMutation.mutate(id)}
            toggleActiveLoading={toggleActiveMutation.isPending}
            syncLdapLoading={syncLdapMutation.isPending}
          />
        )}
      </WidgetCard>

      <UserDrawer
        user={selectedUser}
        onClose={() => setSelectedUser(null)}
        onActionComplete={() => qc.invalidateQueries({ queryKey: ['users'] })}
      />
      <UserCreateModal opened={createOpened} onClose={closeCreate} users={users} />
    </Stack>
  )
}
